import React, { useState, useEffect, useRef } from 'react';
import { Chapter, StoryBible } from '../src/types';
import { loadChapterContentFromStorage, saveEditedChapterToStorage, resolveChapterStoragePath } from '../services/storageChapter.service';
import { resolveChapterTitle } from '../services/chapterTitle.service';
import Tooltip from './Tooltip';
import { isProjectUnlocked } from '../src/utils/entitlements';

interface ManuscriptEditorProps {
  uid: string;
  projectId: string;
  storyBible: StoryBible;
  chapters: Chapter[];
  onUpdateChapters: (updated: Chapter[]) => void;
  onClose: () => void;
  onExport: () => void;
  onShowPaywall: (reason: 'export' | 'generation' | 'autowrite' | 'sequel' | 'rewrite') => void;
}

export const ManuscriptEditor: React.FC<ManuscriptEditorProps> = ({
  uid,
  projectId,
  storyBible,
  chapters,
  onUpdateChapters,
  onClose,
  onExport,
  onShowPaywall
}) => {
  const [selectedChapterId, setSelectedChapterId] = useState<number | null>(chapters.length > 0 ? (chapters[0].chapterNumber || chapters[0].id) : null);
  const [currentText, setCurrentText] = useState<string>('');
  
  // Advanced state
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [originalFinalText, setOriginalFinalText] = useState<string>('');
  const [lastSavedText, setLastSavedText] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [showExportPrompt, setShowExportPrompt] = useState(false);
  
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isFreePreview = !isProjectUnlocked(storyBible);
  const sortedChapters = [...chapters].sort((a,b) => (a.chapterNumber || a.id) - (b.chapterNumber || b.id));

  // Determine actual chapter title
  const getDisplayTitle = (c: Chapter) => {
    const num = c.chapterNumber || c.id;
    const roadmapPlan = storyBible.chapterPlans?.find(p => p.chapterNumber === num);
    return resolveChapterTitle(c, roadmapPlan, num);
  };

  const getLocalBackupKey = (chapNum: number) => `shs_backup_${uid}_${projectId}_${chapNum}`;

  // Load Chapter Text
  useEffect(() => {
    if (!selectedChapterId) return;

    let active = true;
    const loadText = async () => {
      setIsLoading(true);
      setCurrentText('');
      setOriginalFinalText('');
      setLastSavedText('');
      setIsDirty(false);
      setSaveMessage('');
      setSearchQuery('');
      setIsEditMode(false); // default to read mode on load

      try {
        const chap = chapters.find(c => (c.chapterNumber || c.id) === selectedChapterId);
        if (!chap) return;

        let loadedEdited = '';
        let loadedFinal = '';
        
        // 1. Try to load from Storage
        const pathsToTry = [];
        const resolvedPath = resolveChapterStoragePath(chap);
        if (resolvedPath) pathsToTry.push(resolvedPath);
        pathsToTry.push(`users/${uid}/projects/${projectId}/chapters/chapter-${selectedChapterId}.json`);

        for (const path of pathsToTry) {
          try {
             const storageData = await loadChapterContentFromStorage(path);
             if (storageData) {
                loadedEdited = storageData.editedText || '';
                loadedFinal = storageData.finalText || '';
                break;
             }
          } catch (e) {
             // Ignore
          }
        }

        if (!loadedEdited && chap.editedContent) loadedEdited = chap.editedContent;
        if (!loadedFinal && chap.content) loadedFinal = chap.content;

        if (!active) return;

        setOriginalFinalText(loadedFinal);
        const resolvedLastSaved = loadedEdited || loadedFinal || '';
        setLastSavedText(resolvedLastSaved);

        // 3. Check for unsaved local backup
        const localBackup = localStorage.getItem(getLocalBackupKey(selectedChapterId));
        if (localBackup && localBackup !== resolvedLastSaved) {
           setCurrentText(localBackup);
           setIsDirty(true);
           setIsEditMode(true);
           setSaveMessage('Restored unsaved changes from local backup');
        } else {
           setCurrentText(resolvedLastSaved);
           setIsDirty(false);
        }
      } catch (err) {
        console.error("Failed to load chapter text", err);
      } finally {
        if (active) setIsLoading(false);
      }
    };
    loadText();

    return () => { active = false; };
  }, [selectedChapterId, projectId, uid, chapters]);

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    setCurrentText(newText);
    setIsDirty(true);
    setSaveMessage('');
    if (selectedChapterId) {
      localStorage.setItem(getLocalBackupKey(selectedChapterId), newText);
    }
  };

  const handleSave = async (silent = false): Promise<boolean> => {
    if (!selectedChapterId) return false;
    setIsSaving(true);
    if (!silent) setSaveMessage('Saving...');
    try {
       await saveEditedChapterToStorage(uid, projectId, selectedChapterId, currentText);
       
       localStorage.removeItem(getLocalBackupKey(selectedChapterId));
       
       const updated = chapters.map(c => {
         if ((c.chapterNumber || c.id) === selectedChapterId) {
            return { ...c, editedContent: currentText, editedByUser: true, lastEditedAt: new Date().toISOString() };
         }
         return c;
       });
       onUpdateChapters(updated);
       
       setLastSavedText(currentText);
       setIsDirty(false);
       if (!silent) setSaveMessage('Saved successfully.');
       
       setTimeout(() => {
         if (activeSaveTimer.current) setSaveMessage('');
       }, 3000);
       return true;
    } catch (err: any) {
       console.error("Save failed", err);
       setSaveMessage(`Save failed: ${err.message}`);
       return false;
    } finally {
       setIsSaving(false);
    }
  };

  const activeSaveTimer = useRef(true);
  useEffect(() => { return () => { activeSaveTimer.current = false; } }, []);

  const handleSaveAndNext = async () => {
    if (!selectedChapterId) return;
    const currentIndex = sortedChapters.findIndex(c => (c.chapterNumber || c.id) === selectedChapterId);
    if (currentIndex < 0 || currentIndex >= sortedChapters.length - 1) return;
    
    const success = await handleSave(true);
    if (success) {
       const nextId = sortedChapters[currentIndex + 1].chapterNumber || sortedChapters[currentIndex + 1].id;
       selectChapter(nextId, true);
    }
  };

  const handleRestoreDraft = () => {
     if (window.confirm("This will replace the editor text with the original AI generated draft. You must click 'Save Edits' to persist this change. Proceed?")) {
        setCurrentText(originalFinalText);
        setIsDirty(true);
        setIsEditMode(true);
        setSaveMessage('Original draft loaded. Unsaved.');
        if (selectedChapterId) localStorage.setItem(getLocalBackupKey(selectedChapterId), originalFinalText);
     }
  };

  const handleDiscardChanges = () => {
     if (window.confirm("Discard all unsaved changes for this chapter and revert to the last saved version?")) {
        setCurrentText(lastSavedText);
        setIsDirty(false);
        setSaveMessage('Unsaved changes discarded.');
        if (selectedChapterId) localStorage.removeItem(getLocalBackupKey(selectedChapterId));
     }
  };

  const selectChapter = (id: number, overrideDirtyCheck = false) => {
    if (id === selectedChapterId) return;
    if (isDirty && !overrideDirtyCheck) {
      if (!window.confirm("You have unsaved changes. Change chapters anyway? (Changes will remain as a local browser backup)")) {
         return;
      }
    }
    
    if (isFreePreview && id !== 1) {
       onShowPaywall('generation');
       return;
    }
    setSelectedChapterId(id);
  };

  const handleClose = () => {
    if (isDirty) {
      if (!window.confirm("You have unsaved changes. Leave anyway? (Changes will remain as a local browser backup)")) {
         return;
      }
    }
    onClose();
  };

  const escapeRegExp = (string: string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const renderHighlightedText = (text: string, query: string) => {
    if (!query.trim()) return text;
    try {
      const regex = new RegExp(`(${escapeRegExp(query)})`, 'gi');
      const parts = text.split(regex);
      return parts.map((part, index) => 
        part.toLowerCase() === query.toLowerCase() 
          ? <span key={index} className="bg-cv-gold/30 text-cv-warning rounded-[2px] shadow-[0_0_8px_rgba(180,154,99,0.2)]">{part}</span>
          : part
      );
    } catch {
      return text;
    }
  };

  const handleExportClick = () => {
    if (isDirty) {
       setShowExportPrompt(true);
    } else {
       onExport();
    }
  };

  const wordCount = currentText.trim().split(/\s+/).filter(w => w.length > 0).length;
  const matchCount = searchQuery ? (currentText.match(new RegExp(searchQuery, 'gi'))?.length || 0) : 0;
  
  const currentChapterIndex = sortedChapters.findIndex(c => (c.chapterNumber || c.id) === selectedChapterId);
  const hasPrev = currentChapterIndex > 0;
  const hasNext = currentChapterIndex < sortedChapters.length - 1;
  const prevId = hasPrev ? sortedChapters[currentChapterIndex - 1].chapterNumber || sortedChapters[currentChapterIndex - 1].id : null;
  const nextId = hasNext ? sortedChapters[currentChapterIndex + 1].chapterNumber || sortedChapters[currentChapterIndex + 1].id : null;
  
  const currentChapterObj = chapters.find(c => (c.chapterNumber || c.id) === selectedChapterId);
  const isEditedStatus = currentChapterObj?.editedByUser || lastSavedText !== originalFinalText;

  const renderSidebarBadge = (cNum: number, cObj: Chapter) => {
     if (isFreePreview && cNum !== 1) return <span className="text-[9px] uppercase tracking-widest font-black text-cv-muted bg-cv-panel px-1.5 py-0.5 rounded border border-cv-border">Locked</span>;
     
     const hasLocalBackup = localStorage.getItem(getLocalBackupKey(cNum)) !== null;
     if (hasLocalBackup && cNum !== selectedChapterId) return <span className="text-[9px] uppercase tracking-widest font-black text-cv-gold bg-amber-950/50 px-1.5 py-0.5 rounded border border-amber-900/50">Unsaved</span>;
     
     if (cObj.editedByUser) return <span className="text-[9px] uppercase tracking-widest font-black text-cv-gold bg-cv-interactive-soft px-1.5 py-0.5 rounded border border-cv-interactive/30">Edited</span>;
     
     return <span className="text-[9px] uppercase tracking-widest font-black text-cv-muted bg-cv-panel px-1.5 py-0.5 rounded border border-cv-border">Generated</span>;
  };

  return (
    <div className="fixed inset-0 z-[100] bg-cv-page flex flex-col md:flex-row overflow-hidden animate-in fade-in duration-200">
      
      {showExportPrompt && (
        <div className="absolute inset-0 z-50 bg-cv-page/80 backdrop-blur-sm flex items-center justify-center p-4">
           <div className="bg-cv-panel border border-cv-border-strong rounded-xl max-w-md w-full p-6 shadow-2xl">
              <h3 className="text-xl font-black text-cv-primary uppercase tracking-tighter mb-2">Unsaved Edits Detected</h3>
              <p className="text-cv-muted text-sm mb-6">You have unsaved changes in Chapter {selectedChapterId}. How would you like to export?</p>
              <div className="flex flex-col gap-3">
                 <button onClick={() => { setShowExportPrompt(false); handleSave(true).then(() => onExport()); }} className="w-full py-3 bg-cv-interactive hover:bg-cv-interactive-hover text-cv-page font-bold rounded-lg shadow-lg">
                    Save and Export
                 </button>
                 <button onClick={() => { setShowExportPrompt(false); onExport(); }} className="w-full py-3 bg-cv-elevated hover:bg-cv-elevated text-cv-primary font-bold rounded-lg border border-cv-border-strong">
                    Export Last Saved Version
                 </button>
                 <button onClick={() => setShowExportPrompt(false)} className="w-full py-3 text-cv-muted hover:text-cv-primary font-bold transition-colors">
                    Cancel
                 </button>
              </div>
           </div>
        </div>
      )}

      {/* Mobile Top Navigation */}
      <div className="md:hidden border-b border-cv-border bg-cv-panel p-4 flex flex-col gap-3 shrink-0 relative z-20">
         <div className="flex justify-between items-center">
            <h2 className="text-lg font-black text-cv-primary uppercase tracking-tighter flex items-center gap-2">
                Review Workspace
                <Tooltip text="The Manuscript Review Workspace is a safe environment to read and edit your generated chapters before exporting." />
            </h2>
            <button onClick={handleClose} className="p-2 text-cv-muted hover:text-cv-primary rounded-lg bg-cv-elevated">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
         </div>
         <select 
            className="w-full bg-cv-page border border-cv-border-strong rounded-lg p-3 text-cv-primary font-bold"
            value={selectedChapterId || ''}
            onChange={(e) => selectChapter(Number(e.target.value))}
         >
           {sortedChapters.map(c => {
             const cNum = c.chapterNumber || c.id;
             return <option key={cNum} value={cNum}>Chapter {cNum}: {getDisplayTitle(c)}</option>;
           })}
         </select>
         <div className="flex justify-between items-center bg-cv-page p-1.5 rounded-lg border border-cv-border">
            <button disabled={!hasPrev} onClick={() => selectChapter(prevId!)} className="px-3 py-1.5 disabled:opacity-30 text-cv-secondary text-xs font-bold rounded hover:bg-cv-elevated">← Prev</button>
            <div className="text-xs text-cv-muted font-bold uppercase tracking-widest">{selectedChapterId} / {sortedChapters.length}</div>
            <button disabled={!hasNext} onClick={() => selectChapter(nextId!)} className="px-3 py-1.5 disabled:opacity-30 text-cv-secondary text-xs font-bold rounded hover:bg-cv-elevated">Next →</button>
         </div>
      </div>

      {/* Desktop Sidebar */}
      <div className="hidden md:flex w-72 bg-cv-panel border-r border-cv-border flex-col shrink-0 relative z-20">
        <div className="p-6 border-b border-cv-border flex justify-between items-center">
          <h2 className="text-xl font-black text-cv-primary uppercase tracking-tighter leading-tight mb-1 flex items-center gap-2">
              Review<br/>Workspace
              <Tooltip text="The Manuscript Review Workspace is a safe environment to read and edit your generated chapters before exporting." />
          </h2>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
           {sortedChapters.map(c => {
              const cNum = c.chapterNumber || c.id;
              const title = getDisplayTitle(c);
              const isActive = cNum === selectedChapterId;

              return (
                <button
                  key={cNum}
                  onClick={() => selectChapter(cNum)}
                  className={`w-full text-left p-3 rounded-xl transition-all border ${
                    isActive 
                      ? 'bg-cv-interactive text-cv-page border-cv-interactive shadow-lg shadow-black/20'
                      : 'bg-cv-page/50 text-cv-muted border-cv-border hover:bg-cv-elevated hover:text-cv-primary'
                  }`}
                >
                  <div className="text-[10px] uppercase tracking-widest font-bold opacity-80 mb-1 flex items-center justify-between">
                    <span>Chapter {cNum}</span>
                    {renderSidebarBadge(cNum, c)}
                  </div>
                  <div className="text-sm font-medium line-clamp-2">{title}</div>
                </button>
              );
           })}
        </div>
        <div className="p-4 border-t border-cv-border">
           <button onClick={handleClose} className="w-full py-3 text-cv-muted hover:text-cv-primary hover:bg-cv-elevated font-bold text-xs uppercase tracking-widest rounded-lg transition-colors border border-cv-border">
              Return to Dashboard
           </button>
        </div>
      </div>

      {/* Main Area */}
      <div className="flex-1 flex flex-col bg-[#0b1120] relative min-w-0">
        
        {/* Top Action Bar */}
        <div className="h-16 border-b border-cv-border/80 bg-cv-page/80 backdrop-blur-md flex items-center justify-between px-4 md:px-8 shrink-0 relative z-10">
           <div className="flex items-center gap-2 md:gap-4">
              <div className="flex bg-cv-panel border border-cv-border-strong rounded-lg p-1">
                 <button onClick={() => setIsEditMode(false)} className={`px-4 py-1.5 text-xs font-bold rounded-md transition-all ${!isEditMode ? 'bg-cv-elevated text-cv-primary shadow-sm' : 'text-cv-muted hover:text-cv-primary'}`}>
                    Read
                 </button>
                 <button onClick={() => setIsEditMode(true)} className={`px-4 py-1.5 text-xs font-bold rounded-md transition-all ${isEditMode ? 'bg-cv-elevated text-cv-primary shadow-sm' : 'text-cv-muted hover:text-cv-primary'}`}>
                    Edit
                 </button>
              </div>
              
              {/* Simple Search */}
              <div className="hidden lg:flex flex-col relative">
                 <div className="flex items-center bg-cv-panel border border-cv-border rounded-lg px-3 py-1.5 focus-within:border-cv-interactive/50 transition-colors">
                    <svg className="w-3.5 h-3.5 text-cv-muted mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                    <input 
                      type="text" 
                      placeholder="Find... (Ctrl+F)" 
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      className="bg-transparent text-xs text-cv-primary outline-none w-32 placeholder:text-cv-muted"
                    />
                    {searchQuery && <span className="text-[10px] text-cv-muted font-bold ml-2">{matchCount}</span>}
                 </div>
                 {searchQuery && isEditMode && (
                    <div className="absolute top-full right-0 mt-2 bg-cv-panel text-[10px] text-cv-muted p-3 rounded-lg border border-cv-border-strong shadow-xl w-56 z-50">
                       Highlights are available in Read Mode. Edit Mode shows match count only.
                       <button onClick={() => setIsEditMode(false)} className="block mt-2 text-cv-gold hover:text-cv-interactive font-bold tracking-wide">
                         Switch to Read Mode
                       </button>
                    </div>
                 )}
              </div>
           </div>

           <div className="flex items-center gap-2 md:gap-3">
              {isEditMode && isDirty && (
                 <button onClick={handleDiscardChanges} className="hidden md:block px-3 py-2 text-cv-muted hover:text-cv-danger text-xs font-bold uppercase tracking-widest transition-colors">
                    Discard
                 </button>
              )}
              {isEditMode && (
                 <button 
                   onClick={() => handleSave(false)}
                   disabled={isSaving || !isDirty}
                   className="px-4 py-2 bg-cv-elevated hover:bg-cv-elevated disabled:bg-cv-panel disabled:text-cv-disabled text-cv-primary text-xs font-bold uppercase tracking-widest rounded-lg transition-colors border border-cv-border-strong"
                 >
                   {isSaving ? 'Saving...' : 'Save'}
                 </button>
              )}
              {isEditMode && hasNext && (
                 <button 
                   onClick={handleSaveAndNext}
                   disabled={isSaving || (!isDirty && !hasNext)}
                   className="hidden md:block px-4 py-2 bg-cv-interactive hover:bg-cv-interactive-hover disabled:bg-cv-elevated disabled:text-cv-disabled text-cv-page text-xs font-bold uppercase tracking-widest rounded-lg transition-colors border border-cv-interactive shadow-lg shadow-black/20"
                 >
                   Save & Next
                 </button>
              )}
              <button 
                onClick={handleExportClick}
                className="px-4 py-2 bg-cv-interactive hover:bg-cv-interactive-hover text-cv-page text-xs font-bold uppercase tracking-widest rounded-lg transition-colors shadow-lg shadow-black/20"
              >
                Export
              </button>
           </div>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto relative scrollbar-thin scrollbar-thumb-cv-border-strong scrollbar-track-transparent">
           
           {/* Manuscript Header */}
           <div className="max-w-3xl mx-auto px-6 pt-10 pb-6 md:px-12 md:pt-16 md:pb-8">
              <div className="flex justify-between items-start mb-6">
                 <div>
                    <h4 className="text-cv-interactive font-black tracking-widest uppercase text-xs mb-2">
                       Chapter {selectedChapterId}
                    </h4>
                    <h1 className="text-3xl md:text-4xl font-black text-cv-primary tracking-tight leading-none mb-4">
                       {currentChapterObj ? getDisplayTitle(currentChapterObj) : ''}
                    </h1>
                    <div className="flex flex-wrap items-center gap-3 text-xs font-mono">
                       <span className="text-cv-muted">{wordCount} words</span>
                       <span className="text-cv-disabled">•</span>
                       <span className={isEditedStatus ? "text-cv-success" : "text-cv-muted"}>
                          {isEditedStatus ? 'Edited Draft' : 'Unmodified Original'}
                       </span>
                       <span className="text-cv-disabled">•</span>
                       <span className={isDirty ? "text-cv-gold font-bold" : "text-cv-muted"}>
                          {isDirty ? 'Unsaved changes' : 'Saved'}
                       </span>
                    </div>
                 </div>
                 
                 <div className="hidden md:flex gap-2">
                    <button disabled={!hasPrev} onClick={() => selectChapter(prevId!)} className="w-10 h-10 flex items-center justify-center rounded-lg bg-cv-panel border border-cv-border text-cv-muted hover:text-cv-primary disabled:opacity-30 transition-all">
                       <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                    </button>
                    <button disabled={!hasNext} onClick={() => selectChapter(nextId!)} className="w-10 h-10 flex items-center justify-center rounded-lg bg-cv-panel border border-cv-border text-cv-muted hover:text-cv-primary disabled:opacity-30 transition-all">
                       <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                    </button>
                 </div>
              </div>
              
              {saveMessage && (
                 <div className="p-3 bg-cv-panel border border-cv-border rounded-lg text-cv-secondary text-xs font-mono inline-block">
                    {saveMessage}
                 </div>
              )}
           </div>

           <div className="max-w-3xl mx-auto px-6 pb-32 md:px-12">
              {isLoading ? (
                <div className="flex justify-center py-20">
                   <div className="w-8 h-8 border-2 border-cv-interactive/30 border-t-blue-500 rounded-full animate-spin"></div>
                </div>
              ) : isEditMode ? (
                <div className="relative">
                  <textarea
                    ref={textareaRef}
                    value={currentText}
                    onChange={handleTextChange}
                    placeholder="Write your chapter here..."
                    className="w-full min-h-[60vh] bg-cv-panel/30 border border-cv-border/50 rounded-xl text-cv-primary p-6 md:p-8 text-lg leading-relaxed focus:outline-none focus:ring-1 focus:ring-blue-500/50 shadow-inner resize-none"
                    spellCheck={false}
                  />
                  <div className="mt-4 flex justify-end">
                     <button onClick={handleRestoreDraft} className="text-[10px] uppercase tracking-widest font-bold text-cv-muted hover:text-cv-warning transition-colors">
                        Restore Original AI Draft
                     </button>
                  </div>
                </div>
              ) : (
                <div className="text-lg leading-loose text-cv-secondary select-text pb-20">
                   {currentText.split(/\n+/).map((para, idx) => {
                      if (!para.trim()) return null;
                      if (para.trim() === '***' || para.trim() === '---') {
                         return <div key={idx} className="flex justify-center my-8 text-cv-disabled tracking-[0.5em]">***</div>;
                      }
                      return <p key={idx} className="mb-6 indent-8">{renderHighlightedText(para, searchQuery)}</p>;
                   })}
                   {currentText.trim() === '' && (
                      <div className="text-cv-disabled italic text-center py-10">This chapter is currently empty. Switch to Edit Mode to begin writing.</div>
                   )}
                </div>
              )}
           </div>

        </div>
      </div>
    </div>
  );
};
